import { type FormEvent, type JSX, useCallback, useEffect, useState } from 'react';

import {
  type Berichtsabo,
  type Berichtsaboeingabe,
  type Berichtsversandauskunft,
  type Berichtsversanduebersicht,
  type Berichtsversandzeile,
  ERGEBNISNAMEN,
  aboAendern,
  aboAnlegen,
  aboLoeschen,
  versandAuskunft,
  versandUebersicht,
  zeitplanText,
} from '../api/berichte';
import { ApiFehler } from '../api/fehler';
import { Dialog } from '../komponenten/Dialog';
import { Ladeanzeige, useLadestufe } from '../komponenten/Ladestufe';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Skelett } from '../start/Widget';

/**
 * VERSAND NACH ZEITPLAN (AUS-15, Auftrag #30).
 *
 * Im Bericht, für seinen Eigentümer: täglich, wöchentlich oder monatlich zu
 * einer Uhrzeit, als PDF und/oder XLSX, an interne Benutzer — nur an die,
 * die zur Freigabe passen (privat: Sie selbst; Team: Ihr Team). Wer keine
 * Berichte sehen darf, steht zwar da, bekommt aber nichts, und die Maske sagt
 * es. Jeder Empfänger bekommt den Bericht mit SEINEN Rechten (AUS-06).
 *
 * Die letzten Läufe stehen darunter, je Empfänger mit Ergebnis und Grund —
 * Gescheitertes wird nicht still verschluckt (ERR-08).
 */

const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const ZEIT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin' });

function zeitText(iso: string | null): string {
  return iso === null ? '—' : `${ZEIT.format(new Date(iso))} Uhr`;
}

const LEER: Berichtsaboeingabe = {
  rhythmus: 'woechentlich',
  wochentag: 1,
  monatstag: null,
  uhrzeit: '07:00',
  formate: ['pdf'],
  empfaenger: [],
  aktiv: true,
};

function Laeufe({ laeufe }: { readonly laeufe: readonly Berichtsversandzeile[] }): JSX.Element {
  if (laeufe.length === 0) return <p className="hilfe">Noch kein Lauf.</p>;
  return (
    <div className="tabellenhuelle">
      <table className="liste__tabelle">
        <caption className="nur-vorlesen">Letzte Läufe</caption>
        <thead>
          <tr>
            <th scope="col">Lauf</th>
            <th scope="col">Bericht</th>
            <th scope="col">Empfänger</th>
            <th scope="col">Ergebnis</th>
            <th scope="col">Grund</th>
          </tr>
        </thead>
        <tbody>
          {laeufe.map((l) => (
            <tr key={l.id} className={l.ergebnis === 'gescheitert' ? 'versandlauf--gescheitert' : undefined}>
              <td>{zeitText(l.lauf)}</td>
              <td>{l.titel}</td>
              <td>{l.empfaengerName}</td>
              <td>
                <span className={`marke${l.ergebnis === 'gescheitert' ? ' marke--krit' : l.ergebnis === 'uebersprungen' ? ' marke--warn' : ''}`}>
                  {ERGEBNISNAMEN[l.ergebnis]}
                </span>
              </td>
              <td>{l.grund ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Versanddialog({
  bericht,
  beiSchliessen,
}: {
  readonly bericht: string;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [auskunft, setzeAuskunft] = useState<Berichtsversandauskunft | undefined>(undefined);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [hinweis, setzeHinweis] = useState<string | undefined>(undefined);
  const [eingabe, setzeEingabe] = useState<Berichtsaboeingabe>(LEER);
  const [bearbeitet, setzeBearbeitet] = useState<string | null>(null);
  /** CRM-104: das Abo, dessen Löschen im Pop-up nachfragt. */
  const [zuLoeschen, setzeZuLoeschen] = useState<Berichtsabo | null>(null);

  const uebernehmen = useCallback((a: Berichtsversandauskunft): void => {
    setzeAuskunft(a);
    setzeEingabe((e) => (e.empfaenger.length > 0 ? e : { ...e, empfaenger: a.kandidaten.slice(0, 1).map((k) => k.id) }));
  }, []);

  /* Nach Speichern und Loeschen: neu holen, der Fehler steht oben. */
  const laden = useCallback(async (): Promise<void> => {
    try {
      uebernehmen(await versandAuskunft(bericht));
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  }, [bericht, uebernehmen]);

  /* Auftrag CRM-165: drei Stufen statt „wird geladen" — nur fuer das erste Laden. */
  const geladen = useLadestufe((signal) => versandAuskunft(bericht, signal), bericht);
  const ladefehler = geladen.fehler instanceof ApiFehler ? geladen.fehler : undefined;
  useEffect(() => {
    if (geladen.daten !== undefined) uebernehmen(geladen.daten);
  }, [geladen.daten, uebernehmen]);

  const setze = (teil: Partial<Berichtsaboeingabe>): void => setzeEingabe((e) => ({ ...e, ...teil }));
  const name = (id: string): string => auskunft?.kandidaten.find((k) => k.id === id)?.name ?? 'nicht mehr empfangsberechtigt';

  async function speichern(e: FormEvent): Promise<void> {
    e.preventDefault();
    try {
      if (bearbeitet === null) await aboAnlegen(bericht, eingabe);
      else await aboAendern(bericht, bearbeitet, eingabe);
      setzeHinweis(`Versand ${zeitplanText(eingabe)} gespeichert.`);
      setzeBearbeitet(null);
      setzeEingabe({ ...LEER, empfaenger: eingabe.empfaenger });
      setzeFehler(undefined);
      await laden();
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  }

  /** Wirft (CRM-104): Der Fehler steht in der Rückfrage, und sie bleibt offen. */
  async function loeschen(a: Berichtsabo): Promise<void> {
    await aboLoeschen(bericht, a.id);
    setzeZuLoeschen(null);
    setzeHinweis(`Versand ${zeitplanText(a)} gelöscht.`);
    await laden();
  }

  const schalteFormat = (f: 'pdf' | 'xlsx', an: boolean): void =>
    setze({ formate: an ? [...new Set([...eingabe.formate, f])] : eingabe.formate.filter((x) => x !== f) });
  const schalteEmpfaenger = (id: string, an: boolean): void =>
    setze({ empfaenger: an ? [...new Set([...eingabe.empfaenger, id])] : eingabe.empfaenger.filter((x) => x !== id) });

  return (
    <Dialog
      titel="Versand nach Zeitplan"
      unterzeile="Jeder Empfänger bekommt den Bericht mit seinen eigenen Rechten."
      breite="breit"
      beiSchliessen={beiSchliessen}
    >
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {hinweis !== undefined && (
        <p className="hinweiszeile" role="status">
          {hinweis}
        </p>
      )}
      {auskunft === undefined ? (
        <>
          {fehler === undefined && ladefehler !== undefined && <Meldung fehler={ladefehler} />}
          {/* Bis die Antwort uebernommen ist, steht weiter das Skelett. */}
          <Ladeanzeige zustand={geladen} was="Der Versand" skelett={<Skelett art="liste" />}>
            <Skelett art="liste" />
          </Ladeanzeige>
        </>
      ) : (
        <>
          <h3>Abos</h3>
          {auskunft.abos.length === 0 ? (
            <p className="hilfe">Dieser Bericht wird noch nicht verschickt.</p>
          ) : (
            <ul className="anordnung">
              {auskunft.abos.map((a) => (
                <li key={a.id} className="anordnung__zeile">
                  <span className="anordnung__name">
                    {zeitplanText(a)} · {a.formate.map((f) => f.toUpperCase()).join(' und ')} an{' '}
                    {a.empfaenger.map(name).join(', ')} · nächster Lauf {zeitText(a.naechsterLauf)}
                    {!a.aktiv && ' · pausiert'}
                  </span>
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    onClick={() => {
                      setzeBearbeitet(a.id);
                      setzeEingabe({
                        rhythmus: a.rhythmus,
                        wochentag: a.wochentag,
                        monatstag: a.monatstag,
                        uhrzeit: a.uhrzeit,
                        formate: a.formate,
                        empfaenger: a.empfaenger,
                        aktiv: a.aktiv,
                      });
                    }}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="knopf knopf--eingriff knopf--klein"
                    onClick={() => {
                      setzeHinweis(undefined);
                      setzeZuLoeschen(a);
                    }}
                  >
                    Löschen …
                  </button>
                </li>
              ))}
            </ul>
          )}

          <form className="zielwerte__formular" onSubmit={(e) => void speichern(e)} aria-label="Versand einrichten">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="abo-rhythmus">
                Rhythmus
              </label>
              <select
                id="abo-rhythmus"
                className="feld__eingabe"
                value={eingabe.rhythmus}
                onChange={(e) => {
                  const r = e.target.value as Berichtsaboeingabe['rhythmus'];
                  setze({
                    rhythmus: r,
                    wochentag: r === 'woechentlich' ? (eingabe.wochentag ?? 1) : null,
                    monatstag: r === 'monatlich' ? (eingabe.monatstag ?? 1) : null,
                  });
                }}
              >
                <option value="taeglich">Täglich</option>
                <option value="woechentlich">Wöchentlich</option>
                <option value="monatlich">Monatlich</option>
              </select>
            </div>
            {eingabe.rhythmus === 'woechentlich' && (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="abo-wochentag">
                  Wochentag
                </label>
                <select
                  id="abo-wochentag"
                  className="feld__eingabe"
                  value={eingabe.wochentag ?? 1}
                  onChange={(e) => setze({ wochentag: Number(e.target.value) })}
                >
                  {WOCHENTAGE.map((t, i) => (
                    <option key={t} value={i + 1}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {eingabe.rhythmus === 'monatlich' && (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="abo-monatstag">
                  Tag im Monat
                </label>
                <input
                  id="abo-monatstag"
                  className="feld__eingabe"
                  type="number"
                  min={1}
                  max={31}
                  value={eingabe.monatstag ?? 1}
                  onChange={(e) => setze({ monatstag: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })}
                />
              </div>
            )}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="abo-uhrzeit">
                Uhrzeit
              </label>
              <input
                id="abo-uhrzeit"
                className="feld__eingabe"
                type="time"
                value={eingabe.uhrzeit}
                onChange={(e) => e.target.value !== '' && setze({ uhrzeit: e.target.value.slice(0, 5) })}
              />
            </div>
            <fieldset className="feld">
              <legend className="feld__beschriftung">Format</legend>
              {(['pdf', 'xlsx'] as const).map((f) => (
                <label key={f} className="liste__schalter">
                  <input
                    type="checkbox"
                    checked={eingabe.formate.includes(f)}
                    onChange={(e) => schalteFormat(f, e.target.checked)}
                  />{' '}
                  {f.toUpperCase()}
                </label>
              ))}
            </fieldset>
            <fieldset className="feld">
              <legend className="feld__beschriftung">Empfänger</legend>
              {auskunft.kandidaten.map((k) => (
                <label key={k.id} className="liste__schalter">
                  <input
                    type="checkbox"
                    checked={eingabe.empfaenger.includes(k.id)}
                    onChange={(e) => schalteEmpfaenger(k.id, e.target.checked)}
                  />{' '}
                  {k.name}
                  {!k.darfBerichte && (
                    <small className="feld__hilfe"> — ohne Recht auf Berichte, kein Versand</small>
                  )}
                </label>
              ))}
              <small className="feld__hilfe">Angeboten wird, wer zur Freigabe passt.</small>
            </fieldset>
            <label className="liste__schalter">
              <input type="checkbox" checked={eingabe.aktiv} onChange={(e) => setze({ aktiv: e.target.checked })} /> aktiv
            </label>
            <button
              type="submit"
              className="knopf knopf--haupt"
              disabled={eingabe.formate.length === 0 || eingabe.empfaenger.length === 0}
            >
              {bearbeitet === null ? 'Versand einrichten' : 'Änderung speichern'}
            </button>
          </form>

          <h3>Letzte Läufe</h3>
          <Laeufe laeufe={auskunft.laeufe} />
        </>
      )}
      {zuLoeschen !== null && (
        <Rueckfrage
          frage="Versand löschen?"
          unterzeile={`${zeitplanText(zuLoeschen)} · ${zuLoeschen.formate.map((f) => f.toUpperCase()).join(' und ')}`}
          tat="Löschen"
          beiBestaetigen={() => loeschen(zuLoeschen)}
          beiSchliessen={() => setzeZuLoeschen(null)}
        >
          <p>
            Der Bericht geht auf diesem Weg nicht mehr an {zuLoeschen.empfaenger.map(name).join(', ')}. Die
            bisherigen Läufe bleiben in der Liste.
          </p>
        </Rueckfrage>
      )}
    </Dialog>
  );
}

/** Für die Administration: alle Abos des Hauses und die letzten Läufe (Auftrag #30). */
export function Berichtsversanduebersicht(): JSX.Element {
  /* Auftrag CRM-165: drei Stufen statt „wird geladen"; „Erneut versuchen" laedt nur diese Seite. */
  const geladen = useLadestufe((signal) => versandUebersicht(signal));
  const daten: Berichtsversanduebersicht | undefined = geladen.daten;
  const fehler = geladen.fehler instanceof ApiFehler ? geladen.fehler : undefined;

  if (daten === undefined) {
    return (
      <>
        {fehler !== undefined && <Meldung fehler={fehler} />}
        <Ladeanzeige
          zustand={geladen}
          was="Der Berichtsversand"
          skelett={
            <section className="karte">
              <Skelett art="liste" />
            </section>
          }
        />
      </>
    );
  }
  const gescheitert = daten.laeufe.filter((l) => l.ergebnis === 'gescheitert').length;
  return (
    <>
      <section className="karte" aria-labelledby="abos-titel">
        <div className="karte__titel">
          <h2 id="abos-titel">Abos</h2>
          <small>{daten.abos.length} eingerichtet</small>
        </div>
        {daten.abos.length === 0 ? (
          <p className="hilfe">Kein Bericht wird nach Zeitplan verschickt.</p>
        ) : (
          <div className="tabellenhuelle">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">Alle Abos</caption>
              <thead>
                <tr>
                  <th scope="col">Bericht</th>
                  <th scope="col">Eigentümer</th>
                  <th scope="col">Zeitplan</th>
                  <th scope="col">Format</th>
                  <th scope="col" className="zahl">
                    Empfänger
                  </th>
                  <th scope="col">Nächster Lauf</th>
                  <th scope="col">Letzter Lauf</th>
                </tr>
              </thead>
              <tbody>
                {daten.abos.map((a) => (
                  <tr key={a.id}>
                    <th scope="row">{a.titel}</th>
                    <td>{a.eigentuemer}</td>
                    <td>
                      {zeitplanText(a)}
                      {!a.aktiv && ' · pausiert'}
                    </td>
                    <td>{a.formate.map((f) => f.toUpperCase()).join(', ')}</td>
                    <td className="zahl">{a.empfaenger.length}</td>
                    <td>{zeitText(a.naechsterLauf)}</td>
                    <td>{zeitText(a.letzterLauf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="karte" aria-labelledby="laeufe-titel">
        <div className="karte__titel">
          <h2 id="laeufe-titel">Letzte Läufe</h2>
          <small>{gescheitert === 0 ? 'nichts gescheitert' : `${String(gescheitert)} gescheitert`}</small>
        </div>
        <Laeufe laeufe={daten.laeufe} />
      </section>
    </>
  );
}
